import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { uploadProgressCallCsv } from '../../../services/apiService';

const CALL_TYPES = {
  WELCOME_CALL: 'Welcome Call',
  WELCOME_FOLLOW_UP: 'Welcome Follow-up',
  PROGRESS_CALLS: 'Progress Calls',
  ALMAX_ELIGIBILITY_CALL: 'AlmaX Eligibility Call',
  FOUNDATION_TRACK_COMPLETION_CALL: 'Foundation Track Completion Call',
};

const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB in bytes

const UploadTab = () => {
  const [file, setFile] = useState('');
  const [loading, setLoading] = useState(false);
  const [callType, setCallType] = useState('');
  const [callValue, setCallValue] = useState('');
  const fileRef = useRef(null);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > MAX_FILE_SIZE) {
        toast.error(`File size exceeds the limit of ${formatFileSize(MAX_FILE_SIZE)}`);
        return;
      }
      setFile(e.target.files);
    }
  };

  const handleUpload = async (e) => {
    e?.preventDefault(); // Prevent any default form submission

    if (!callType) {
      toast.error('Please select a call type');
      return;
    }

    if (!callValue) {
      toast.error('Please enter the required value');
      return;
    }

    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    if (loading) return; // Prevent multiple calls while loading

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file[0]);
      formData.append('type', callType);
      formData.append('name', callValue);

      const response = await uploadProgressCallCsv(formData);
      if (response) {
        toast.success(response?.message);
        // Reset form after successful upload
        setFile('');
        setCallType('');
        setCallValue('');
        if (fileRef.current) {
          fileRef.current.value = ''; // Clear the file input
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error?.response?.data?.message ||
    error?.message ||
    'File upload failed. Please try again.'|| 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className='bg-white p-4 border-b border-solid border-gray-400'>
        <form onSubmit={handleUpload} className='flex items-center gap-4'>
          {/* Upload Name Field */}
          <div className='w-[180px]'>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              Upload Name
            </label>
            <select
              value={callType}
              onChange={(e) => setCallType(e.target.value)}
              className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500'
            >
              <option value=''>Select Type</option>
              {Object.values(CALL_TYPES).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Value Input Field */}
          <div className='w-[180px]'>
            <input
              type='text'
              value={callValue}
              onChange={(e) => setCallValue(e.target.value)}
              placeholder='Enter name'
              className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 mt-6'
            />
          </div>

          {/* File Upload */}
          <div className='w-[300px]'>
            <div className='flex items-center justify-between mb-1'>
              <label className='text-sm font-medium text-gray-700'>
                File Upload
              </label>
              <span className='text-xs text-gray-500 ml-4'>
                Max size: {formatFileSize(MAX_FILE_SIZE)}
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <input
                type='file'
                ref={fileRef}
                onChange={handleFileChange}
                className='hidden'
                accept='.csv'
              />
              <div className='flex-1 flex items-center gap-2'>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className='flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-md border border-gray-300 min-w-[200px]'
                >
                  {file?.[0]?.name || 'Choose File'}
                </button>
              </div>
            </div>
          </div>

          {/* Upload Button */}
          <div className='flex items-center gap-2 mt-6'>
            {file && !loading && (
              <button
                type="submit"
                className='bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md'
              >
                Upload
              </button>
            )}
            {loading && (
              <span className='text-blue-500 px-4 py-2 flex items-center'>
                Processing...
              </span>
            )}
          </div>

          {/* Download Template Button - Right Aligned */}
          <div className='ml-auto mt-6'>
            <a
              href="https://ab-public-bucket-prod.s3.ap-south-1.amazonaws.com/progressuUploadTemplate.csv"
              target='_blank'
              rel='noreferrer'
              download
              className='whitespace-nowrap bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-2 rounded-md border border-blue-300'
            >
              Download Template
            </a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadTab; 